/**
 * Typed external references for integration payloads.
 *
 * Integration plugins carry provider entity ids — a Slack member `U…` or
 * channel `C…` id, a Linear user or team UUID — inside flow trigger
 * payloads, action params, and other wire types. Naming the type
 * (`SlackUserId`, `SlackChannelId`, `LinearUserId` via
 * `ExternalRefId<'slack', 'channel'>`) keeps those ids from degrading into
 * anonymous strings, and the paired schema factory stamps the field with a
 * `fieldType` of `'<provider>:<kind>'`. That one annotation — the same
 * namespace the flow conditions editor's option pickers key off — is what
 * lets generic surfaces (run views' badges, payload dumps) recognize the
 * field without knowing the plugin.
 *
 * Resolving ids into display profiles is a server concern: plugins register
 * an `ExternalRefResolver` per fieldType (see `@reflex/plugin-api/services`)
 * that queries their provider's API, and web surfaces fetch through the
 * flows external-refs route.
 *
 * Browser-safe: this module depends on zod only.
 *
 * @module @reflex/plugin-api/external-refs
 */
import { z } from 'zod';

/** Provider namespaces and ref kinds are lower-kebab; the web's inverse
 * parser (`parseExternalRefFieldType`) accepts exactly this shape, so
 * minting any other casing would silently produce badge-less fields. */
const SEGMENT_RE = /^[a-z][a-z0-9-]*$/;

/**
 * The semantic `fieldType` marking a payload field as referencing one kind
 * of provider entity (e.g. `'linear:user'`, `'slack:channel'`). Option
 * providers and external-ref resolvers key off the same namespace. Throws
 * on a malformed segment so the mistake surfaces at plugin module load,
 * not as silently unrecognized fields.
 */
export function externalRefFieldType<P extends string, K extends string>(
  provider: P,
  kind: K,
): `${P}:${K}` {
  if (!SEGMENT_RE.test(provider) || !SEGMENT_RE.test(kind)) {
    throw new Error(
      `External-ref provider and kind must be lower-kebab (e.g. 'slack' + 'channel', ` +
        `'incident-io' + 'user'); got "${provider}:${kind}"`,
    );
  }
  return `${provider}:${kind}`;
}

/** User-kind sugar for the most common reference: `'<provider>:user'`. */
export function externalUserFieldType<P extends string>(provider: P): `${P}:user` {
  return externalRefFieldType(provider, 'user');
}

/**
 * Nominal id for one kind of external-provider entity. A `SlackChannelId`
 * is not assignable from a bare string, a Reflex id, or a `SlackUserId`;
 * values are minted by parsing with `externalRefIdSchema` at the wire
 * boundary.
 */
export type ExternalRefId<P extends string = string, K extends string = string> = string &
  z.$brand<`${P}:${K}-id`>;

/** Nominal id for a user of one external provider. */
export type ExternalUserId<P extends string = string> = ExternalRefId<P, 'user'>;

/**
 * Schema factory for a provider entity-id field. Parsing brands the output
 * as `ExternalRefId<P, K>`, and the baked `fieldType` metadata survives
 * `.optional()` / `.describe()` / further `.meta()` calls into
 * `z.toJSONSchema()` output, which is how the event and action catalogs
 * advertise the field to the web.
 */
export function externalRefIdSchema<P extends string, K extends string>(provider: P, kind: K) {
  return z
    .string()
    .min(1)
    .brand<`${P}:${K}-id`>()
    .meta({ fieldType: externalRefFieldType(provider, kind) });
}

/** User-kind sugar: `externalRefIdSchema(provider, 'user')`. */
export function externalUserIdSchema<P extends string>(provider: P) {
  return externalRefIdSchema(provider, 'user');
}

/**
 * List variant for fields like `assigneeLogins`. The fieldType is stamped
 * on the array itself (where JSON-Schema consumers look for the property's
 * annotation) as well as riding each element via the inner schema.
 */
export function externalRefIdListSchema<P extends string, K extends string>(provider: P, kind: K) {
  return z
    .array(externalRefIdSchema(provider, kind))
    .meta({ fieldType: externalRefFieldType(provider, kind) });
}

/**
 * Inverse of {@link externalRefFieldType}: `'slack:channel'` →
 * `{ provider: 'slack', kind: 'channel' }`. Returns null for anything
 * outside the namespace so callers can filter a JSON Schema's properties
 * down to external references.
 */
export function parseExternalRefFieldType(
  fieldType: string,
): { provider: string; kind: string } | null {
  const match = /^([a-z][a-z0-9-]*):([a-z][a-z0-9-]*)$/.exec(fieldType);
  return match ? { provider: match[1], kind: match[2] } : null;
}

/**
 * Display profile for one external entity, as resolved by the owning
 * plugin's registered resolver. `id` is the raw provider id the caller
 * asked about; everything else is presentation data for badges and
 * popovers.
 */
export const ExternalRefProfileSchema = z.object({
  /** Provider namespace the id belongs to ('slack', 'linear', …). */
  provider: z.string().min(1),
  /** Entity kind within the provider ('user', 'channel', 'team', …). */
  kind: z.string().min(1),
  /** The raw provider id that was resolved. */
  id: z.string().min(1),
  /** Primary display text — a person's name, `#channel`, a team name. */
  displayName: z.string().min(1),
  /** Secondary muted line (a channel topic, a team key). */
  subtitle: z.string().optional(),
  /** Provider handle when one exists (a Slack `@name`). Users only. */
  handle: z.string().optional(),
  email: z.string().optional(),
  // Both URL fields end up in DOM sinks (img src, anchor href). Resolver
  // data is third-party API output, so the contract pins them to https —
  // a future resolver mapping a user-controlled field here must not be
  // able to smuggle a javascript: link into the popover.
  /** Omitted when the entity has no image — UIs render initials. */
  avatarUrl: z.url({ protocol: /^https$/ }).optional(),
  /** Deep link to the entity in the provider's own app. */
  url: z.url({ protocol: /^https$/ }).optional(),
});
export type ExternalRefProfile = z.infer<typeof ExternalRefProfileSchema>;

/**
 * Prototype-safe lookup of one resolved profile. Ids come from webhook
 * payloads, so a value like `"constructor"` must miss instead of walking
 * the prototype chain into a function the chip would try to render.
 */
export function externalRefProfileFor(
  refs: { profiles: Record<string, ExternalRefProfile> } | undefined,
  id: string,
): ExternalRefProfile | null {
  if (!refs || !Object.hasOwn(refs.profiles, id)) return null;
  const profile = refs.profiles[id];
  return profile && typeof profile === 'object' ? profile : null;
}
